import {
  Box,
  Flex,
  Text,
  VStack,
  useColorModeValue,
  Divider,
} from '@chakra-ui/react';

import type { QuoteSummaryLocation, TaxFee } from 'api/quote-marketplace';
import { formatCurrency } from '../../constants';
import { calculatePremiumBreakdown } from '../../utils/premiumCalculations';

interface LocationSummarySectionProps {
  locations: QuoteSummaryLocation[];
  annualPremium: string | null;
  policyPremium: string | null;
  taxes: TaxFee[] | null;
}

const getLocationTypeLabel = (locationType: string): string => {
  const typeMap: Record<string, string> = {
    primary_residence: 'Primary Residence',
    seasonal: 'Seasonal',
    secondary: 'Secondary',
    rental: 'Rental',
    vacant: 'Vacant',
  };
  return typeMap[locationType] || locationType || 'Location';
};

const LocationSummarySection = ({
  locations,
  annualPremium,
  policyPremium,
  taxes,
}: LocationSummarySectionProps) => {
  const borderColor = useColorModeValue('gray.200', 'gray.600');
  const headerBg = useColorModeValue('gray.50', 'gray.700');

  const {
    annualPremium: premiumNum,
    taxTotal,
    taxPercentage,
  } = calculatePremiumBreakdown(annualPremium, taxes);

  const formatAddress = (location: QuoteSummaryLocation): string => {
    const parts = [location.address, location.city, location.province].filter(
      Boolean,
    );
    return parts.join(', ') || 'No address';
  };

  return (
    <Box mb={6}>
      <Flex
        bg={headerBg}
        px={3}
        py={2}
        borderWidth="1px"
        borderColor={borderColor}
        borderTopRadius="md"
        justify="space-between"
      >
        <Text fontWeight="600" fontSize="sm">
          Locations
        </Text>
        <Text fontWeight="600" fontSize="sm">
          Coverage
        </Text>
      </Flex>

      <Box
        borderWidth="1px"
        borderTopWidth="0"
        borderColor={borderColor}
        borderBottomRadius="md"
        p={3}
      >
        <VStack spacing={2} align="stretch">
          {locations.map((location) => (
            <Flex key={location.id} justify="space-between" align="center">
              <Text fontSize="sm" color="gray.700">
                {getLocationTypeLabel(location.location_type)} -{' '}
                {formatAddress(location)}
              </Text>
              <Text fontSize="sm" fontWeight="500">
                {location.building_coverage
                  ? formatCurrency(String(location.building_coverage))
                  : '-'}
              </Text>
            </Flex>
          ))}

          <Flex
            justify="space-between"
            align="center"
            pt={3}
            mt={2}
            borderTopWidth="1px"
            borderColor={borderColor}
          >
            <Text fontSize="sm" color="gray.600">
              Estimated Annual Premium
            </Text>
            <Text fontSize="sm">
              {formatCurrency(policyPremium ?? annualPremium)}
            </Text>
          </Flex>
          <Divider variant="dashed" />
          <Flex justify="space-between" align="center">
            <Text fontSize="sm" color="gray.600">
              {taxPercentage}% Tax Applied
            </Text>
            <Text fontSize="sm">{formatCurrency(String(taxTotal))}</Text>
          </Flex>

          <Flex
            justify="space-between"
            align="center"
            pt={3}
            mt={2}
            borderTopWidth="1px"
            borderColor={borderColor}
          >
            <Text fontSize="sm" fontWeight="600">
              Estimated Total Premium
            </Text>
            <Text fontSize="sm" fontWeight="600">
              {formatCurrency(String(premiumNum))}
            </Text>
          </Flex>
        </VStack>
      </Box>
    </Box>
  );
};

export default LocationSummarySection;
